import { createCalendarField } from '../base/calendar-field.base';
import { createCheckboxField } from '../base/checkbox-field.base';
import { createDateField } from '../base/date-field.base';
import { createNumberField } from '../base/number-field.base';
import { createRadioButtonGroup } from '../base/radio-button-group.base';
import { createRadioCardField } from '../base/radio-card-field.base';
import { createSelectField } from '../base/select-field.base';
import { createTextField } from '../base/text-field.base';
import { createTextareaField } from '../base/textarea-field.base';
import { createUploadField } from '../base/upload-field.base';

/**
 * Factory function para criar form fields com o contexto correto
 *
 * @param useFieldContext - Hook de contexto do formulário específico (quotes, proposals, etc.)
 * @returns Objeto com todos os form fields configurados para aquele contexto
 *
 * @example
 * ```ts
 * // quotes/hooks/form-fields.ts
 * import { createFormFields } from '@/shared/components/form-fields/factories';
 * import { useFieldContext } from './form-context.hook';
 *
 * export const QuoteFormFields = createFormFields(useFieldContext);
 * ```
 */
export function createFormFields(useFieldContext: () => unknown) {
  return {
    TextField: createTextField(useFieldContext),
    TextareaField: createTextareaField(useFieldContext),
    NumberField: createNumberField(useFieldContext),
    DateField: createDateField(useFieldContext),
    CheckboxField: createCheckboxField(useFieldContext),
    SelectField: createSelectField(useFieldContext),
    RadioButtonGroup: createRadioButtonGroup(useFieldContext),
    RadioCardField: createRadioCardField(useFieldContext),
    UploadField: createUploadField(useFieldContext),
    CalendarField: createCalendarField(useFieldContext),
  };
}

// Re-export types para conveniência
export type { TextFieldProps } from '../base/text-field.base';

export type { TextareaFieldProps } from '../base/textarea-field.base';

export type { UploadedFile, UploadFieldProps } from '../base/upload-field.base';

export type { NumberFieldProps } from '../base/number-field.base';

export type { DateFieldProps } from '../base/date-field.base';

export type { CheckboxFieldProps } from '../base/checkbox-field.base';

export type { CalendarFieldProps } from '../base/calendar-field.base';

export type {
  SelectFieldOption,
  SelectFieldProps
} from '../base/select-field.base';

export type {
  RadioButtonGroupProps,
  RadioButtonOption
} from '../base/radio-button-group.base';

export type {
  RadioCardFieldProps,
  RadioCardOption
} from '../base/radio-card-field.base';

